import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { useState } from "react";
import { API_BASE_URL } from "../constants/apiBase";
import { getAppCheckToken, getIdToken } from "../firebase";

export interface ApiFileResult {
  isLoading: boolean;
  error: string | null;
  executeFileFetch: (
    url: string,
    method: "GET" | "POST",
  ) => Promise<Blob | null>;
}

const API_KEY = process.env.REACT_APP_API_KEY;

function useFileFetch(): ApiFileResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeFileFetch = async (
    url: string,
    method: "GET" | "POST",
  ): Promise<Blob | null> => {
    setIsLoading(true);

    const token = await getIdToken();

    // Get App Check token
    const appCheckToken = await getAppCheckToken();

    const headers: Record<string, string> = {
      accept: "*/*",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
      "Api-key": API_KEY || "",
      "Content-Type": "application/octet-stream", // Expecting a file
    };

    const options: AxiosRequestConfig = {
      url: `${API_BASE_URL}${url}`,
      method,
      headers,
      responseType: "blob", // 👈 Important: Ensures binary data is received correctly
    };

    try {
      const response = await axios(options);
      setError(null);
      setIsLoading(false);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        setError(
          `Feil: ${error.response.status} - Kunne ikke laste ned filen.`,
        );
      } else {
        setError("En ukjent feil oppstod under nedlasting av filen.");
      }
      setIsLoading(false);
    }

    return null;
  };

  return {
    isLoading,
    error,
    executeFileFetch,
  };
}

export default useFileFetch;

